import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Phone, CheckCircle2, AlertTriangle, Loader2, Link2, Unlink, RefreshCw, Stethoscope, XCircle, Wifi, WifiOff } from "lucide-react";
import { useSipPhone } from "@/components/sales/SipPhoneProvider";
import AppLayout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntegrationsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [devices, setDevices] = useState([]);
  const [diagnose, setDiagnose] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const sip = useSipPhone();

  const sipgateParam = searchParams.get("sipgate");
  const msg = searchParams.get("msg");

  useEffect(() => {
    if (sipgateParam === "connected") {
      toast.success("Sipgate erfolgreich verbunden");
      setSearchParams({});
    } else if (sipgateParam === "error") {
      toast.error(`Sipgate-Verbindung fehlgeschlagen: ${msg || "Unbekannter Fehler"}`);
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sipgateParam]);

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/integrations/sipgate/status`, { withCredentials: true });
      setStatus(res.data);
      if (res.data.connected) {
        try {
          const dev = await axios.get(`${API}/integrations/sipgate/devices`, { withCredentials: true });
          setDevices(dev.data?.devices || []);
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      toast.error("Status konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/integrations/sipgate/auth-url`, { withCredentials: true });
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verbindung konnte nicht gestartet werden");
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Sipgate wirklich trennen?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/integrations/sipgate/disconnect`, {}, { withCredentials: true });
      toast.success("Sipgate getrennt");
      setStatus({ ...status, connected: false });
      setDevices([]);
      setDiagnose(null);
    } catch (e) {
      toast.error("Trennen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const runDiagnose = async () => {
    setDiagnosing(true);
    try {
      const res = await axios.get(`${API}/integrations/sipgate/diagnose`, { withCredentials: true });
      setDiagnose(res.data);
      toast.success("Diagnose abgeschlossen");
    } catch (e) {
      toast.error("Diagnose fehlgeschlagen");
    } finally {
      setDiagnosing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="integrations-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Integrationen</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Verbinde externe Tools mit signalSolution.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin" size={16}/> Lädt...</div>
        ) : (
          <>
            {/* Sipgate Card */}
            <div className="border border-border rounded-lg bg-card p-6 mb-6" data-testid="sipgate-card">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-11 h-11 rounded-md bg-cyan-500/15 text-cyan-500 flex items-center justify-center shrink-0">
                  <Phone size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-semibold">Sipgate</h2>
                    {status?.connected ? (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm bg-green-500/15 text-green-500 border border-green-500/30" data-testid="sipgate-connected-badge">
                        <CheckCircle2 size={11}/> Verbunden
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm bg-amber-500/15 text-amber-500 border border-amber-500/30">
                        <AlertTriangle size={11}/> Nicht verbunden
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Telefonie aus dem CRM. Click-to-Call, Anruf-Logs und automatisches Tracking der Gesprächsdauer.
                  </p>
                </div>
              </div>

              {/* Connection Info */}
              {status?.connected && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 text-xs">
                  <div className="bg-muted/50 rounded-md p-3">
                    <div className="text-muted-foreground uppercase tracking-wider mb-1">Scopes</div>
                    <div className="font-mono break-all">{status.scope || "-"}</div>
                  </div>
                  <div className="bg-muted/50 rounded-md p-3">
                    <div className="text-muted-foreground uppercase tracking-wider mb-1">Verbunden seit</div>
                    <div className="font-mono">
                      {status.connected_at ? new Date(status.connected_at).toLocaleString("de-DE") : "-"}
                    </div>
                  </div>
                </div>
              )}

              {/* Devices */}
              {status?.connected && devices.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Verfügbare Geräte</div>
                  <div className="space-y-1.5">
                    {devices.map((d) => (
                      <div key={d.id} className="text-sm flex items-center gap-2 bg-muted/30 rounded-sm px-3 py-2">
                        <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded-sm">{d.id}</span>
                        <span>{d.alias || d.type}</span>
                        {d.online && <span className="ml-auto text-xs text-green-500">● online</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Browser-Phone Section */}
              {status?.connected && (
                <div className="mb-5 p-4 rounded-md border border-cyan-500/20 bg-cyan-500/5" data-testid="sip-browser-phone-card">
                  <div className="flex items-center gap-2 mb-2">
                    {sip.registered ? <Wifi size={14} className="text-green-500"/> : <WifiOff size={14} className="text-muted-foreground"/>}
                    <span className="text-sm font-semibold">Browser-Telefon</span>
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                      sip.registered ? "bg-green-500/15 text-green-500" :
                      sip.registering ? "bg-amber-500/15 text-amber-500" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      {sip.registered ? "Verbunden" : sip.registering ? "Verbinde…" : "Getrennt"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Aktiviere das Browser-Telefon, um Anrufe direkt im Tab durchzuführen — kein zusätzliches Programm nötig. Bleibt aktiv solange der Tab offen ist.
                  </p>
                  <div className="flex gap-2 items-center flex-wrap">
                    {!sip.registered && (
                      <Button size="sm" onClick={sip.connect} disabled={sip.registering} data-testid="browser-phone-connect-btn">
                        {sip.registering ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <Link2 size={13} className="mr-1.5"/>}
                        Telefon verbinden
                      </Button>
                    )}
                    {sip.registered && sip.credentials && (
                      <span className="text-xs text-muted-foreground font-mono">
                        Gerät: {sip.credentials.device_id} · {sip.credentials.alias}
                      </span>
                    )}
                    {sip.error && (
                      <span className="text-xs text-red-500">{sip.error}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 flex-wrap">
                {status?.connected ? (
                  <>
                    <Button variant="outline" onClick={loadStatus} disabled={busy} data-testid="sipgate-refresh-btn">
                      <RefreshCw size={14} className="mr-2"/> Status aktualisieren
                    </Button>
                    <Button variant="outline" onClick={runDiagnose} disabled={diagnosing} data-testid="sipgate-diagnose-btn">
                      {diagnosing ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Stethoscope size={14} className="mr-2"/>}
                      Diagnose ausführen
                    </Button>
                    <Button variant="destructive" onClick={handleDisconnect} disabled={busy} data-testid="sipgate-disconnect-btn">
                      <Unlink size={14} className="mr-2"/> Trennen
                    </Button>
                  </>
                ) : (
                  <Button onClick={handleConnect} disabled={busy || !status?.configured} data-testid="sipgate-connect-btn">
                    {busy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Link2 size={14} className="mr-2"/>}
                    Sipgate verbinden
                  </Button>
                )}
                {!status?.configured && (
                  <span className="text-xs text-amber-500">
                    SIPGATE_CLIENT_ID / SECRET nicht in der .env hinterlegt
                  </span>
                )}
              </div>

              {/* Diagnose Output */}
              {diagnose && (
                <div className="mt-6 border-t border-border pt-5" data-testid="sipgate-diagnose-output">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Stethoscope size={14}/> Diagnose-Ergebnis
                  </h3>
                  <div className="space-y-2">
                    {(diagnose.checks || []).map((c, i) => (
                      <div key={i} className={`text-xs p-2.5 rounded-md border ${
                        c.ok
                          ? "bg-green-500/5 border-green-500/20 text-green-200"
                          : "bg-red-500/5 border-red-500/20 text-red-200"
                      }`}>
                        <div className="flex items-center gap-2">
                          {c.ok ? <CheckCircle2 size={12} className="text-green-500"/> : <XCircle size={12} className="text-red-500"/>}
                          <span className="font-mono font-bold">{c.test}</span>
                          <span className="ml-auto text-[10px] uppercase tracking-wider">
                            {c.status ?? (c.error ? "ERR" : "?")}
                          </span>
                        </div>
                        {c.body && typeof c.body === "object" && (
                          <pre className="mt-2 text-[10px] opacity-70 font-mono whitespace-pre-wrap break-all">
                            {JSON.stringify(c.body, null, 2)}
                          </pre>
                        )}
                        {c.body && typeof c.body === "string" && (
                          <div className="mt-1 text-[10px] opacity-70 font-mono">{c.body}</div>
                        )}
                        {c.devices && c.devices.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {c.devices.map((d) => (
                              <div key={d.id} className="text-[10px] flex items-center gap-2 font-mono">
                                <span className="bg-white/10 px-1.5 py-0.5 rounded-sm">{d.id}</span>
                                <span>{d.alias || d.type}</span>
                                <span className={`ml-auto ${d.online ? "text-green-400" : "text-red-400"}`}>
                                  {d.online ? "● online" : "● offline"}
                                </span>
                                <span className={d.registered ? "text-green-400" : "text-amber-400"}>
                                  {d.registered ? "registered" : "not registered"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {c.numbers && c.numbers.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {c.numbers.map((n) => (
                              <div key={n.id} className="text-[10px] font-mono opacity-80">
                                {n.number} {n.endpointId ? `→ ${n.endpointId}` : "(nicht zugewiesen)"}
                              </div>
                            ))}
                          </div>
                        )}
                        {c.error && <div className="mt-1 text-[10px]">{c.error}</div>}
                      </div>
                    ))}
                  </div>

                  {diagnose.token_info && (
                    <div className="mt-3 text-[10px] font-mono text-muted-foreground bg-muted/30 p-2 rounded-sm">
                      Scopes: {(diagnose.token_info.scopes || []).join(", ")}<br/>
                      Token läuft in {diagnose.token_info.expires_in_sec}s ab
                    </div>
                  )}
                </div>
              )}

              {/* Setup Instructions */}
              {!status?.connected && status?.configured && (
                <div className="mt-6 border-t border-border pt-5">
                  <h3 className="text-sm font-semibold mb-3">So richtest du Sipgate ein:</h3>
                  <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                    <li>Im <a href="https://app.sipgate.com" target="_blank" rel="noreferrer" className="text-cyan-500 underline">Sipgate-Webaccount</a> unter <b>Account &gt; Webhooks</b> die folgende URL eintragen:
                      <code className="block mt-1 text-xs bg-muted p-2 rounded-sm font-mono break-all">{(process.env.REACT_APP_BACKEND_URL || "")}/api/integrations/sipgate/webhook</code>
                      (Events <b>Hangup</b>, <b>Newcall</b>, <b>Answer</b> aktivieren)
                    </li>
                    <li>Unter <b>Account &gt; Web-API &gt; OAuth2-Clients</b> bei deiner App folgende Redirect-URI hinzufügen:
                      <code className="block mt-1 text-xs bg-muted p-2 rounded-sm font-mono break-all">{status?.redirect_uri}</code>
                    </li>
                    <li>Mindestens ein <b>Telefongerät</b> (z.B. Webphone <code>e0</code> oder Tischtelefon) im Sipgate-Account einrichten.</li>
                    <li>Telefonie-Tarif (<b>Sipgate Team / Trunking / Basic</b>) muss aktiv sein – ohne Tarif keine Click-to-Call API.</li>
                    <li>Dann oben auf <b>"Sipgate verbinden"</b> klicken und im Sipgate-Login authentifizieren.</li>
                  </ol>
                </div>
              )}
            </div>
          </>
        )}
      </div>
  );
}
